import { useSelector } from 'react-redux';
import { startCase } from 'lodash';
import {
  OnboardingChrome,
  DurationSlideOutContent,
  CareHoursStartSlideOutContent,
  CareHoursEndSlideOutContent,
  Badge,
} from '../components';
import { selectClient, selectIntakeSurvey } from '../model';
import { possessivePronoun } from '../util';
import { SlideContent, SlideOutSheet } from '../components';

import { BsFillQuestionCircleFill } from 'react-icons/bs';
import {
  carePhaseDefs,
  formatDurationYears,
  CarePhase,
  isDurationCustom,
} from '@shared';

export function OnboardingPredictedCareNeeds(_props: ClientIdProps) {
  const { clientFirstName } = useSelector(selectClient);

  return (
    <OnboardingChrome
      slidePage={OnboardingPredictedCareNeeds}
      nextButtonLabel={`See ${clientFirstName}'s Costs`}
    >
      <SlideContent headerText={`${clientFirstName}'s predicted care needs`}>
        <div className="flex flex-col gap-4 pb-6 pt-6 lg:flex-row lg:gap-5 lg:pb-12 lg:pt-12">
          <ColumnLengthOfCareYears />
          <ColumnCareHoursLtcStart />
          <ColumnCareHoursLtcEnd />
        </div>
      </SlideContent>
    </OnboardingChrome>
  );
}

function ColumnLengthOfCareYears() {
  const intakeSurvey = useSelector(selectIntakeSurvey);
  const {
    phaseCosts,
    allPhaseCosts: { allPhaseCareMonthsNeeded },
  } = useSelector(selectClient);
  const ltcDurationYears = allPhaseCareMonthsNeeded / 12;
  return (
    <div
      className="flex-grow-1 border-primary-200 basis-1/2 rounded-2xl border p-6 text-center"
      data-testid="predicted-care-needs-length"
    >
      <div
        className="text-base font-medium text-gray-800 md:text-xl"
        data-testid="predicted-duration-label"
      >
        {startCase(possessivePronoun(intakeSurvey))} length of care:
        <SlideOutSheet
          slideOutContent={<DurationSlideOutContent />}
          title="AI"
        >
          <BsFillQuestionCircleFill
            className="h-5 w-5 pb-1 pl-1 text-gray-400 hover:text-gray-500"
            aria-hidden="true"
          />
        </SlideOutSheet>
      </div>
      <div
        className="text-purple text-6xl font-semibold leading-[1.5] md:text-9xl"
        data-testid="predicted-duration-value"
      >
        {formatDurationYears(ltcDurationYears)}
      </div>
      <div
        className="text-base font-normal text-gray-700 md:text-xl"
        data-testid="predicted-duration-unit"
      >
        years
        {isDurationCustom(phaseCosts) && (
          <Badge
            color="gray"
            label="edited"
            className="ml-1"
          />
        )}
      </div>
    </div>
  );
}

function ColumnCareHoursLtcStart() {
  const intakeSurvey = useSelector(selectIntakeSurvey);
  const { phaseCosts } = useSelector(selectClient);
  const phaseIndex = carePhaseDefs[CarePhase.earlyCare].index;
  const earlyCarePhaseCosts = phaseCosts[phaseIndex];
  const { phaseCareHoursNeeded, phaseCareMonthsNeeded } = earlyCarePhaseCosts;
  const weeksPerMonth = 4.33;
  const hoursPerWeek =
    phaseCareHoursNeeded / phaseCareMonthsNeeded / weeksPerMonth;

  return (
    <div
      className="flex-grow-1 border-primary-200 basis-1/2 rounded-2xl border p-6 text-center"
      data-testid="predicted-care-needs-initial-hours"
    >
      <div
        className="text-base font-medium text-gray-800 md:text-xl"
        data-testid="predicted-initial-hours-label"
      >
        {startCase(possessivePronoun(intakeSurvey))} initial care needs:
        <SlideOutSheet
          slideOutContent={<CareHoursStartSlideOutContent />}
          title="AI"
        >
          <BsFillQuestionCircleFill
            className="h-5 w-5 pb-1 pl-1 text-gray-400 hover:text-gray-500"
            aria-hidden="true"
          />
        </SlideOutSheet>
      </div>
      <div
        className="text-purple text-6xl font-semibold leading-[1.5] md:text-9xl"
        data-testid="predicted-initial-hours-value"
      >
        {Math.round(hoursPerWeek)}
      </div>
      <div
        className="text-base font-normal text-gray-700 md:text-xl"
        data-testid="predicted-initial-hours-unit"
      >
        hours/week
      </div>
    </div>
  );
}

function ColumnCareHoursLtcEnd() {
  const { phaseCosts } = useSelector(selectClient);
  const intakeSurvey = useSelector(selectIntakeSurvey);
  const phaseIndex = carePhaseDefs[CarePhase.fullCare].index;
  const earlyCarePhaseCosts = phaseCosts[phaseIndex];
  const { phaseCareHoursNeeded, phaseCareMonthsNeeded } = earlyCarePhaseCosts;
  const weeksPerMonth = 4.33;
  const hoursPerWeek =
    phaseCareHoursNeeded / phaseCareMonthsNeeded / weeksPerMonth;

  return (
    <div
      className="flex-grow-1 border-primary-200 basis-1/2 rounded-2xl border p-6 text-center"
      data-testid="predicted-care-needs-later-hours"
    >
      <div
        className="text-base font-medium text-gray-800 md:text-xl"
        data-testid="predicted-later-hours-label"
      >
        {startCase(possessivePronoun(intakeSurvey))} later care needs:
        <SlideOutSheet
          slideOutContent={<CareHoursEndSlideOutContent />}
          title="AI"
        >
          <BsFillQuestionCircleFill
            className="h-5 w-5 pb-1 pl-1 text-gray-400 hover:text-gray-500"
            aria-hidden="true"
          />
        </SlideOutSheet>
      </div>
      <div
        className="text-purple text-6xl font-semibold leading-[1.5] md:text-9xl"
        data-testid="predicted-later-hours-value"
      >
        {Math.round(hoursPerWeek)}
      </div>
      <div
        className="text-base font-normal text-gray-700 md:text-xl"
        data-testid="predicted-later-hours-unit"
      >
        hours/week
      </div>
    </div>
  );
}
